import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Select, Button } from "@/components/controls";
import { Upload, Download, ArrowUpFromLine, ArrowDownFromLine, ShieldAlert } from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";

export default function Imports() {
  const [accounts, setAccounts] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [accountId, setAccountId] = useState("");
  const [marketplace, setMarketplace] = useState("Amazon");
  const [chinaShipmentId, setChinaShipmentId] = useState("");
  const [chinaShipments, setChinaShipments] = useState([]);
  const [exportFmt, setExportFmt] = useState("csv");
  const [forceOverride, setForceOverride] = useState(false);
  const [lastMismatches, setLastMismatches] = useState(null);
  const { isAdmin } = usePerms();

  useEffect(() => {
    Promise.all([api.get("/accounts"), api.get("/settings/dropdowns")]).then(([a, d]) => {
      setAccounts(a.data); setDropdowns(d.data);
      if (a.data.length) setAccountId(a.data[0].account_id);
    });
    // Load distinct China Shipment IDs so the export card shows a dropdown
    // instead of a free-text prompt.
    api.get("/inbound/shipments").then(({ data }) => setChinaShipments(data || [])).catch(() => {});
  }, []);

  const [activeJob, setActiveJob] = useState(null); // {job_id, endpoint, ...poll status}

  const doUpload = async (endpoint, file, params) => {
    const form = new FormData();
    form.append("file", file);
    // Only admin can enable update_existing; UI hides the toggle for non-admins.
    if (isAdmin && forceOverride) params = { ...params, update_existing: true };
    try {
      const { data } = await api.post(endpoint, form, { params, headers: { "Content-Type": "multipart/form-data" } });
      // Legacy sync response (no job_id) — show summary immediately.
      if (!data.queued && !data.job_id) {
        const mm = data.mismatches || [];
        if (mm.length) setLastMismatches({ endpoint, rows: mm, total: data.total_mismatches ?? mm.length });
        else setLastMismatches(null);
        const parts = [];
        if (data.created != null) parts.push(`created ${data.created}`);
        if (data.updated != null) parts.push(`updated ${data.updated}`);
        if (data.skipped != null) parts.push(`skipped ${data.skipped}`);
        if (mm.length) parts.push(`⚠ ${mm.length} account/marketplace mismatch(es)`);
        toast.success(parts.join(" · ") || `Done — ${JSON.stringify(data)}`);
        return;
      }
      // New async job flow — start polling for progress
      toast.info(`Import queued (${data.total} rows). Watch the progress bar below.`);
      setActiveJob({
        job_id: data.job_id,
        endpoint,
        total: data.total,
        processed: 0,
        status: "running",
        created: 0, updated: 0, skipped: 0,
        log: [],
      });
      const pollId = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/imports/status/${data.job_id}`);
          setActiveJob(s);
          if (s.status !== "running") {
            clearInterval(pollId);
            const r = s.result || {};
            if (s.status === "failed") {
              toast.error(`Import failed: ${s.error || "unknown"}`);
            } else {
              const mm = r.mismatches || [];
              if (mm.length) setLastMismatches({ endpoint, rows: mm, total: r.total_mismatches ?? mm.length });
              else setLastMismatches(null);
              const parts = [];
              if (r.created != null) parts.push(`created ${r.created}`);
              if (r.updated != null) parts.push(`updated ${r.updated}`);
              if (r.skipped != null) parts.push(`skipped ${r.skipped}`);
              if (r.already_populated_count) parts.push(`⚠ ${r.already_populated_count} already-populated`);
              if (mm.length) parts.push(`⚠ ${mm.length} mismatch(es)`);
              toast.success(parts.join(" · ") || "Import complete");
            }
          }
        } catch { /* keep polling — transient errors OK */ }
      }, 1500);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCard = (key, title, desc, tpl, upload, needsScope = false, refObj = { current: null }) => (
    <div key={key} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col" data-testid={`import-card-${key}`}>
      <div className="font-heading text-[13px] font-semibold text-slate-900">{title}</div>
      <div className="text-[11.5px] text-slate-500 mt-1 mb-3 flex-1">{desc}</div>
      <div className="flex gap-2">
        <Button onClick={() => downloadFile(tpl, `${key}_template.csv`)} testId={`tpl-${key}`}>
          <Download size={13} /> Template
        </Button>
        <label className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-slate-200 cursor-pointer bg-slate-900 text-white hover:bg-slate-800" data-testid={`upload-${key}`}>
          <Upload size={13} /> Upload
          <input ref={(el) => (refObj.current = el)} type="file" accept=".csv,.txt,.xlsx,.xls" onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (needsScope && !accountId) { toast.error("Pick an Account ID + Marketplace first"); return; }
            upload(f).finally(() => { if (refObj.current) refObj.current.value = ""; });
          }} className="hidden" />
        </label>
      </div>
    </div>
  );

  const exportCard = (key, title, desc, url, filterInput = null, multiSheet = false) => (
    <div key={key} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col" data-testid={`export-card-${key}`}>
      <div className="font-heading text-[13px] font-semibold text-slate-900">{title}</div>
      <div className="text-[11.5px] text-slate-500 mt-1 mb-3 flex-1">{desc}</div>
      {filterInput}
      {multiSheet ? (
        <div className="flex gap-2">
          <Button variant="primary"
            onClick={async () => {
              try {
                await downloadFile(url + (url.includes("?") ? "&" : "?") + "format=xlsx", `${key}.xlsx`);
                toast.success("Downloaded workbook with one sheet per Account ID");
              } catch (e) { toast.error(String(e.message || e)); }
            }}
            testId={`export-${key}-xlsx`}>
            <ArrowDownFromLine size={13} /> Download XLSX (per-account sheets)
          </Button>
          <Button
            onClick={async () => {
              try {
                await downloadFile(url + (url.includes("?") ? "&" : "?") + "format=txt", `${key}.zip`);
              } catch (e) { toast.error(String(e.message || e)); }
            }}
            testId={`export-${key}-zip`}>
            <ArrowDownFromLine size={13} /> Download ZIP (.txt per account)
          </Button>
        </div>
      ) : (
        <Button variant="primary"
          onClick={async () => {
            try {
              await downloadFile(url + (url.includes("?") ? "&" : "?") + `format=${exportFmt}`, `${key}.${exportFmt}`);
            } catch (e) { toast.error(String(e.message || e)); }
          }}
          testId={`export-${key}`}>
          <ArrowDownFromLine size={13} /> Download ({exportFmt})
        </Button>
      )}
    </div>
  );

  return (
    <>
      <PageHeader title="Imports & Exports" subtitle="Bulk order intake + operational templates" testId={T.moduleTitle("imports")} />
      <PageBody>
        {/* Live progress panel — only shown while an import job is running or just-completed */}
        {activeJob && (
          <div className="bg-white border-2 border-blue-300 rounded-sm p-4 mb-4" data-testid="import-progress-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[12px] font-semibold text-slate-900">
                {activeJob.status === "running" ? "Importing" : activeJob.status === "failed" ? "Import failed" : "Import complete"}
                <span className="ml-2 text-[11px] text-slate-500 font-normal font-mono">{activeJob.endpoint || activeJob.filename}</span>
              </div>
              {activeJob.status !== "running" && (
                <button onClick={() => setActiveJob(null)} className="text-[11px] text-slate-500 hover:underline">dismiss</button>
              )}
            </div>
            <div className="flex justify-between text-[11px] font-mono text-slate-700 mb-1">
              <span>Row {activeJob.processed ?? 0} / {activeJob.total ?? 0}</span>
              <span>{activeJob.total ? Math.round(((activeJob.processed ?? 0) / activeJob.total) * 100) : 0}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
              <div className={`h-full transition-all ${activeJob.status === "failed" ? "bg-red-500" : activeJob.status === "done" ? "bg-emerald-500" : "bg-blue-500"}`}
                    style={{ width: `${activeJob.total ? ((activeJob.processed ?? 0) / activeJob.total) * 100 : 0}%` }} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px] font-mono mt-2">
              <div><span className="text-slate-500">Created:</span> <span className="font-semibold text-emerald-700">{activeJob.created ?? 0}</span></div>
              <div><span className="text-slate-500">Updated:</span> <span className="font-semibold text-blue-700">{activeJob.updated ?? 0}</span></div>
              <div><span className="text-slate-500">Skipped:</span> <span className="font-semibold text-amber-700">{activeJob.skipped ?? 0}</span></div>
              <div><span className="text-slate-500">Errors:</span> <span className="font-semibold text-red-700">{activeJob.total_errors ?? 0}</span></div>
              <div><span className="text-slate-500">Mismatches:</span> <span className="font-semibold text-red-700">{activeJob.total_mismatches ?? 0}</span></div>
            </div>
            {activeJob.status === "failed" && activeJob.error && (
              <div className="mt-2 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5 font-mono">
                {activeJob.error}
              </div>
            )}
            {activeJob.status !== "running" && (activeJob.total_errors ?? 0) > 0 && activeJob.job_id && (
              <div className="mt-2">
                <Button
                  onClick={() => downloadFile(`/imports/errors/${activeJob.job_id}.csv`, `import_errors_${activeJob.job_id}.csv`)}
                  testId="download-import-errors"
                >
                  <Download size={13} /> Download unmatched rows report ({activeJob.total_errors} row{activeJob.total_errors === 1 ? "" : "s"})
                </Button>
              </div>
            )}
            {activeJob.status !== "running"
              && (activeJob.result?.already_populated_count ?? 0) > 0
              && activeJob.endpoint === "shipping-update"
              && activeJob.job_id && (
              <div className="mt-2">
                <Button
                  onClick={() => downloadFile(
                    `/imports/shipping-update/skipped/${activeJob.job_id}.csv`,
                    `shipping_update_skipped_${activeJob.job_id}.csv`,
                  )}
                  testId="download-shipping-update-skipped"
                >
                  <Download size={13} /> Download already-populated rows ({activeJob.result.already_populated_count} row{activeJob.result.already_populated_count === 1 ? "" : "s"})
                </Button>
              </div>
            )}
            {activeJob.log && activeJob.log.length > 0 && (
              <details className="mt-2" open={activeJob.status === "running"}>
                <summary className="text-[10.5px] font-semibold text-slate-600 cursor-pointer hover:text-slate-800">
                  Per-row log ({activeJob.log.length} entries — showing last 20)
                </summary>
                <div className="mt-1 max-h-48 overflow-y-auto bg-slate-50 border border-slate-200 rounded-sm p-1.5 font-mono text-[10.5px] space-y-0.5">
                  {activeJob.log.slice(-20).reverse().map((l, i) => (
                    <div key={i} className={l.status === "error" ? "text-red-700" : l.status === "skipped" ? "text-amber-700" : "text-slate-700"}>
                      row {l.row}: {l.msg}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
        {/* Scope selector for scoped imports */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
          <Field label="Account ID">
            <Select testId={T.formInput("import-account")} options={accounts.map((a) => a.account_id)} value={accountId} onChange={setAccountId} />
          </Field>
          <Field label="Marketplace">
            <Select testId={T.formInput("import-marketplace")} options={dropdowns.marketplace || []} value={marketplace} onChange={setMarketplace} allowEmpty={false} />
          </Field>
          <Field label="Export format">
            <Select testId={T.formInput("export-format")} options={["csv", "xlsx", "txt"]} value={exportFmt} onChange={setExportFmt} allowEmpty={false} />
          </Field>
          {isAdmin && (
            <div className="md:col-span-3 flex items-start gap-2 mt-1 pt-3 border-t border-slate-100">
              <input
                type="checkbox"
                id="force-override-toggle"
                data-testid="import-force-override"
                checked={forceOverride}
                onChange={(e) => setForceOverride(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-red-600"
              />
              <label htmlFor="force-override-toggle" className="text-[12px] text-slate-700 leading-relaxed cursor-pointer">
                <span className="inline-flex items-center gap-1 font-semibold text-red-700">
                  <ShieldAlert size={12} /> ADMIN OVERRIDE
                </span>{" "}
                — Force update existing orders even when Account&nbsp;ID or Marketplace on the file doesn't match the current record.
                Use this only when a wrong file was originally imported and the record must be re-scoped.
              </label>
            </div>
          )}
        </div>

        {lastMismatches && (
          <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 mb-4" data-testid="mismatches-panel">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert size={14} className="text-amber-700" />
              <div className="font-heading text-[13px] font-semibold text-amber-900">
                {lastMismatches.total} row(s) skipped — Account&nbsp;ID / Marketplace mismatch on existing records
              </div>
              <button onClick={() => setLastMismatches(null)} className="ml-auto text-[11px] text-amber-800 hover:underline">dismiss</button>
            </div>
            <div className="text-[11px] text-amber-900 mb-2">
              These records already exist under a different scope. Only an ADMIN can enable the override checkbox above to re-scope them.
            </div>
            <div className="max-h-40 overflow-auto border border-amber-200 rounded-sm bg-white">
              <table className="erp-table">
                <thead><tr><th>Row</th><th>Order Item ID</th><th>Field</th><th>Existing</th><th>Incoming</th></tr></thead>
                <tbody>
                  {lastMismatches.rows.flatMap((m, i) => {
                    const rows = [];
                    ["account_id", "marketplace"].forEach((k) => {
                      if (m[k]) rows.push(
                        <tr key={`${i}-${k}`}>
                          <td className="font-mono">{m.row}</td>
                          <td className="font-mono truncate max-w-[180px]">{m.order_item_id}</td>
                          <td>{k}</td>
                          <td className="text-red-700 line-through">{String(m[k].existing)}</td>
                          <td className="text-emerald-700">{String(m[k].incoming)}</td>
                        </tr>
                      );
                    });
                    return rows;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mb-2 flex items-center gap-2">
          <ArrowUpFromLine size={14} className="text-slate-500" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Imports</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {uploadCard("orders", "Marketplace Orders", "Upload Amazon flat-file. Duplicates by Order Item ID skipped.",
            "/imports/templates/orders",
            (f) => doUpload("/imports/orders", f, { account_id: accountId, marketplace }),
            true)}
          {uploadCard("sellerflex-orders", "Sellerflex Orders",
            "Amazon Sellerflex 60-col report. Creates orders (order_item_id key). Duplicates skipped.",
            "/imports/templates/sellerflex-orders",
            (f) => doUpload("/imports/sellerflex-orders", f, { account_id: accountId }),
            true)}
          {uploadCard("flipkart-orders", "Flipkart Orders",
            "Flipkart 37-col report. Creates orders (order_item_id key). Duplicates skipped.",
            "/imports/templates/flipkart-orders",
            (f) => doUpload("/imports/flipkart-orders", f, { account_id: accountId }),
            true)}
          {uploadCard("marketplace-identifier", "Marketplace Identifier",
            "Drop Amazon's native 59-col 'All Orders' report — only `sku` and `asin` are read. Uses the Account ID above to scope updates.",
            "/imports/templates/marketplace-identifier",
            (f) => doUpload("/imports/marketplace-identifier", f, { account_id: accountId }),
            true)}
          {uploadCard("shipping-update", "Shipping Update",
            "Bulk update shipping_status, courier, tracking + date by System ID.",
            "/imports/templates/shipping-update",
            (f) => doUpload("/imports/shipping-update", f, {}))}
          {uploadCard("sellerflex-shipping-update", "Sellerflex Shipping Update",
            "Amazon Sellerflex Customer Shipments report. Matches Customer Order ID → order_id; when Shipment Tracking ID is present, flips shipping_status + marketplace_status to Shipped (courier = Amazon).",
            "/imports/templates/sellerflex-shipping-update",
            (f) => doUpload("/imports/sellerflex-shipping-update", f, {}))}
          {uploadCard("order-status-update", "Order Status Update",
            "Update marketplace_status by System ID.",
            "/imports/templates/order-status-update",
            (f) => doUpload("/imports/order-status-update", f, {}))}
          {uploadCard("orders-meta-update", "Update Created Date + Order Item ID",
            "Bulk-update the Created Date and/or Order Item ID on existing orders, keyed by System ID (e.g. BLR14014). CSV columns: system_id, created_date, order_item_id. Accepts dd-mmm-yyyy, Aug 02 2026, yyyy-mm-dd, dd/mm/yyyy.",
            "/imports/templates/orders-meta-update",
            (f) => doUpload("/imports/orders-meta-update", f, {}))}
        </div>

        <div className="mb-2 flex items-center gap-2">
          <ArrowDownFromLine size={14} className="text-slate-500" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Exports</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {exportCard("daily-shipping", "Daily Shipping",
            "Shiprocket 54-col export of orders where working_status = Daily-Ship.",
            "/exports/daily-shipping")}
          {exportCard("china-shipment", "China Shipment",
            "Shiprocket 54-col export for a specific China Shipment ID (marketplace + shipping status empty).",
            `/exports/china-shipment?china_shipment_id=${encodeURIComponent(chinaShipmentId)}`,
            <div className="mb-2">
              <select value={chinaShipmentId} onChange={(e) => setChinaShipmentId(e.target.value)}
                     data-testid="export-china-shipment-id"
                     className="w-full h-8 text-[12px] font-mono border border-slate-200 rounded-sm px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">— Select China Shipment ID —</option>
                {chinaShipments.map((s) => (
                  <option key={s.china_shipment_id} value={s.china_shipment_id}>
                    {s.china_shipment_id} · {s.total_rows} row{s.total_rows === 1 ? "" : "s"}
                    {s.pending_rows ? ` · ${s.pending_rows} pending` : ""}
                  </option>
                ))}
              </select>
              {!chinaShipments.length && (
                <div className="text-[10.5px] text-slate-400 mt-1">No shipments yet — assign china_shipment_id on orders first.</div>
              )}
            </div>)}
          {exportCard("amazon-shipping", "Amazon Shipping",
            "Amazon MFN 8-col export of shipped-but-not-yet-marketplace-confirmed orders. XLSX gives one sheet per Account ID; CSV/TXT gives a zip with one file per account.",
            "/exports/amazon-shipping",
            null,
            true /* forceXlsx hint — see exportCard */)}
        </div>
      </PageBody>
    </>
  );
}
